import { ApplicationInsights, IPageViewTelemetry, Util } from "@microsoft/applicationinsights-web";
import { noop } from "lodash";
import { accountSettingsApi } from "../../Redux/AccountSettings/api/accountSettingsApi";
import { licenseApi } from "../../Redux/License/api/licenseApi";
import { LicenseTypes } from "../../Redux/License/const/index";

const needTelemetry =
    ep.settings !== undefined && // note: will not send telemetry from login page
    ep.settings.currentUserId !== undefined &&
    ep.settings.currentUserId !== null &&
    (!ep.settings.isDebug || localStorage.getItem("enableAudit"));

const appInsights = needTelemetry
    ? new ApplicationInsights({
          config: {
              instrumentationKey: "AuditKey",
              endpointUrl: `${ep.appRoot}services/audit/log`,
              enableAutoRouteTracking: false,
          },
      })
    : ({
          loadAppInsights: noop,
          setAuthenticatedUserContext: noop,
          trackEvent: noop,
          trackTrace: noop,
          trackMetric: noop,
          trackException: noop,
          trackPageView: noop,
      } as ApplicationInsights);

const addHistoryListener = needTelemetry
    ? () => {
          // eslint-disable-next-line max-len
          // https://github.com/microsoft/ApplicationInsights-JS/blob/master/extensions/applicationinsights-analytics-js/src/JavaScriptSDK/ApplicationInsights.ts#L618
          const namePrefix = "appInsight.";
          const history = window.history;
          history.pushState = (f =>
              function pushState() {
                  const ret = f.apply(this, arguments);
                  window.dispatchEvent(Util.createDomEvent(namePrefix + "pushState"));
                  window.dispatchEvent(Util.createDomEvent(namePrefix + "locationchange"));
                  return ret;
              })(history.pushState);

          history.replaceState = (f =>
              function replaceState() {
                  const ret = f.apply(this, arguments);
                  window.dispatchEvent(Util.createDomEvent(namePrefix + "replaceState"));
                  window.dispatchEvent(Util.createDomEvent(namePrefix + "locationchange"));
                  return ret;
              })(history.replaceState);

          if (window.addEventListener) {
              window.addEventListener(namePrefix + "popstate", () => {
                  window.dispatchEvent(Util.createDomEvent(namePrefix + "locationchange"));
              });

              window.addEventListener(namePrefix + "locationchange", () => {
                  if (appInsights.context && appInsights.context.telemetryTrace) {
                      appInsights.context.telemetryTrace.traceID = Util.generateW3CId();
                      appInsights.context.telemetryTrace.name = location.pathname;
                  }
              });
          }
      }
    : noop;
addHistoryListener();

appInsights.loadAppInsights();
appInsights.setAuthenticatedUserContext(
    "" + (ep.settings && `${ep.settings.accountName}_${ep.settings.currentUserId}`) + "",
    "" + (ep.settings && ep.settings.accountName) + ""
);

appInsights.trackMetric(
    {
        name: "Screen resolution metric",
        average: screen.width,
    },
    {
        "Screen Resolution": screen.width + "x" + screen.height,
        "Screen Width": screen.width,
        "Screen Height": screen.height,
    }
);

const getAccountSettings = async () => {
    // we always have accounts settings on spa
    const accountSettingsFromState = window.store?.getState().accountSettings;
    if (accountSettingsFromState) {
        return accountSettingsFromState;
    }

    // fallback on some separate pages, like user onboarding
    const { AccountSettings } = await accountSettingsApi.getAccountSettings();
    return AccountSettings;
};

const trackAIPageView = async (pageView: IPageViewTelemetry) => {
    const license = await licenseApi.get();
    const isTrialLicense = license.LicenseType === LicenseTypes.HostingTrial;

    const { IsSalesAssisted, DontUseInStats } = await getAccountSettings();

    pageView.properties = {
        isTrialLicense,
        IsSalesAssisted,
        DontUseInStats,
        IsBirdview: ep.settings.isBirdviewBrand,
        ...pageView.properties,
    };

    appInsights.trackPageView(pageView);
};

/**
 * Tracks AI event if specified
 *
 * @param event {string}
 * @param customProperties {{
 *      [key: string]: any;
 *  }}
 */
const trackAIEvent = async (event: string, customProperties?: object) => {
    if (event && needTelemetry) {
        const license = await licenseApi.get();
        const isTrialLicense = license.LicenseType === LicenseTypes.HostingTrial;
        const { IsSalesAssisted, DontUseInStats } = await getAccountSettings();

        const aiEvent = {
            name: event,
            properties: {
                ...customProperties,
                isTrialLicense,
                IsSalesAssisted,
                DontUseInStats,
                IsBirdview: ep.settings.isBirdviewBrand,
            },
        };

        appInsights.trackEvent(aiEvent);
    }
};

export { trackAIEvent, trackAIPageView };
